/**
 * The whole component set.
 *
 * The 2023 build pulled in Semantic UI for four buttons and three modals, which meant a
 * large stylesheet, a fixed visual language that had to be fought, and a dependency that
 * has since stopped being maintained. This is what it was actually using.
 */

import type { ButtonHTMLAttributes, ReactNode, SelectHTMLAttributes } from 'react';

type ButtonTone = 'primary' | 'quiet' | 'danger';

const TONES: Record<ButtonTone, string> = {
  primary: 'bg-accent text-accent-ink hover:brightness-110 border-transparent',
  quiet: 'bg-surface text-ink border-rule hover:border-rule-strong hover:bg-raised',
  danger:
    'bg-transparent text-player-1 border-rule hover:bg-raised hover:border-player-1',
};

export function Button({
  tone = 'quiet',
  className = '',
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { tone?: ButtonTone }) {
  return (
    <button
      type='button'
      className={`inline-flex items-center justify-center gap-2 rounded-md border px-3 py-1.5
        text-sm font-medium transition-colors disabled:cursor-not-allowed
        disabled:opacity-40 ${TONES[tone]} ${className}`}
      {...rest}
    />
  );
}

export function Panel({
  title,
  children,
  className = '',
  actions,
}: {
  title?: ReactNode;
  children: ReactNode;
  className?: string;
  actions?: ReactNode;
}) {
  return (
    <section
      className={`flex min-h-0 flex-col rounded-lg border border-rule bg-surface ${className}`}
    >
      {title && (
        <header className='flex items-center justify-between gap-2 border-b border-rule px-3 py-2'>
          <h2 className='font-mono text-[0.7rem] font-medium tracking-[0.12em] text-muted uppercase'>
            {title}
          </h2>
          {actions}
        </header>
      )}
      {children}
    </section>
  );
}

export function Field({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <label className='flex flex-col gap-1'>
      <span className='font-mono text-[0.68rem] tracking-[0.1em] text-muted uppercase'>
        {label}
      </span>
      {children}
    </label>
  );
}

export function Select(
  { className = '', ...rest }: SelectHTMLAttributes<HTMLSelectElement>,
) {
  return (
    <select
      className={`rounded-md border border-rule bg-surface px-2 py-1.5 text-sm text-ink
        ${className}`}
      {...rest}
    />
  );
}

/** A player's colour as a small square. Used wherever a name needs a side attached. */
export function Seat(
  { player, className = '' }: { player: 0 | 1; className?: string },
) {
  return (
    <span
      className={`inline-block size-2.5 shrink-0 rounded-[2px] ${
        player === 0 ? 'bg-player-0' : 'bg-player-1'
      } ${className}`}
      aria-hidden='true'
    />
  );
}

export function Badge({
  children,
  tone = 'muted',
}: {
  children: ReactNode;
  tone?: 'muted' | 'accent' | 'alert';
}) {
  const tones = {
    muted: 'bg-raised text-muted',
    accent: 'bg-accent-soft text-accent',
    alert: 'bg-alert-soft text-alert',
  };
  return (
    <span
      className={`rounded px-1.5 py-0.5 font-mono text-[0.68rem] tracking-wide ${
        tones[tone]
      }`}
    >
      {children}
    </span>
  );
}

/** Empty states carry their own instruction; a blank panel explains nothing. */
export function Empty({ children }: { children: ReactNode }) {
  return (
    <p className='px-3 py-6 text-center text-sm text-balance text-muted'>
      {children}
    </p>
  );
}
